import React, { useEffect, useMemo, useState } from "react";
import { useLivePrices, mergeLiveIntoAlert } from "../hooks/useLivePrices";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, TrendingUp, Activity } from "lucide-react";
import Navbar from "../components/Navbar";
import SignalBadge from "../components/SignalBadge";
import AlertClassBadge from "../components/AlertClassBadge";
import AlertStatusBadge from "../components/AlertStatusBadge";
import PriceContinuum from "../components/PriceContinuum";
import SectorStrip from "../components/SectorStrip";
import HudCorners from "../components/HudCorners";
import MacroBriefing from "../components/MacroBriefing";
import ScorePill from "../components/ScorePill";
import { NeutralMarketTag } from "../components/NeutralMarketTag";
import RecentExits from "../components/RecentExits";
// SpeculativeStrip retired 2026-02 (Spike Radar killed — unbacktested).
import SharePanel from "../components/SharePanel";
import { Button } from "../components/ui/button";
import { api, auth as authLib } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Time-of-day greeting — market lives on New York time, so we anchor
// there rather than the visitor's local clock (a subscriber in Tokyo
// checking premarket US futures still gets "Morning" alongside the desk).
export function greetingFor(date = new Date()) {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", { hour: "numeric", hour12: false, timeZone: "America/New_York" }).format(date)
  );
  if (hour >= 5 && hour < 12) return "Morning";
  if (hour >= 12 && hour < 17) return "Afternoon";
  if (hour >= 17 && hour < 22) return "Evening";
  return "Late night";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [regime, setRegime] = useState(null);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [topPicks, setTopPicks] = useState([]);
  const [feedTab, setFeedTab] = useState("active");
  const [clock, setClock] = useState(formatClock());
  // Cached summary of the user's saved custom-portfolio scenario.
  // null = not loaded yet, false = user hasn't built one, object = summary.
  const [customSummary, setCustomSummary] = useState(null);

  // A user is a subscriber if any of the following holds:
  //  - role is "admin" (always in)
  //  - role is "subscriber" (legacy hardcoded role)
  //  - `subscribed === true` on the fresh /auth/me user doc (Stripe-paid users
  //    whose role stays "user" but whose `subscribed` flag flips when the
  //    Stripe webhook lands). AuthContext now re-fetches /auth/me on mount so
  //    a webhook that lands AFTER login still promotes the session correctly.
  const isSubscriber = user && (
    user.role === "admin" ||
    user.role === "subscriber" ||
    user.subscribed === true
  );
  const isAdmin = user?.role === "admin";

  const load = async ({ fresh = false } = {}) => {
    setLoadingFeed(true);
    // Fire a global refresh event so tape-driven components (SectorStrip, etc.)
    // re-fetch against a fresh compute — otherwise clicking "Refresh" would
    // reload the alert feed but leave the sector pills stuck on stale data.
    try { window.dispatchEvent(new CustomEvent("mb:refresh-tape")); } catch { /* fire-and-forget */ }
    try {
      const [r1, r2] = await Promise.all([
        api.get("/screener/regime"),
        api.get("/screener/run"),
      ]);
      setRegime(r1.data);
      setTopPicks((r2.data.buy_candidates || []).slice(0, 5));
    } catch (e) {/* ignore */}
    if (isSubscriber) {
      try {
        // fresh=true busts the 10-min daily-bar cache AND overlays a live
        // intraday tick on every ACTIVE alert so "Refresh" actually shows
        // the current tape (fixes "alert green today but dashboard says red").
        const r = await api.get(`/alerts/feed${fresh ? "?fresh=true" : ""}`);
        setFeed(r.data || []);
      } catch (e) {
        console.error("alerts feed load failed", e?.response?.status, e?.response?.data);
      }
    }
    setLoadingFeed(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isSubscriber) { setCustomSummary(false); return; }
    api.get("/portfolio/custom/summary")
      .then((r) => setCustomSummary(r.data?.saved || false))
      .catch(() => setCustomSummary(false));
  }, [isSubscriber]);
  useEffect(() => {
    const id = setInterval(() => setClock(formatClock()), 1000);
    return () => clearInterval(id);
  }, []);

  const { activeFresh, activeStale, historyClosed } = useMemo(() => {    const isClosedStatus = (s) => ["WIN_TP","WIN_MOON","WIN_TIME","LOSS_SL","LOSS_TIME"].includes(s);
    const fresh = []; const stale = []; const closed = [];
    for (const a of feed) {
      const live = a.live || {};
      if (isClosedStatus(live.status)) { closed.push(a); continue; }
      const isStale = live.entry_zone === "chasing" || live.tp1_hit;
      if (isStale) stale.push(a); else fresh.push(a);
    }
    return { activeFresh: fresh, activeStale: stale, historyClosed: closed };
  }, [feed]);

  // ─── Live price ticker (Feb 2026) ────────────────────────────────
  // Poll /api/prices every 30s for symbols with OPEN alerts so the
  // dashboard ticks like Schwab. Closed alerts don't need live prices
  // (their final return is banked). Stock detail page has its own poll.
  const openSymbols = useMemo(
    () => [...activeFresh, ...activeStale].map(a => a.symbol),
    [activeFresh, activeStale],
  );
  const { prices: livePrices, lastFetch: liveLastFetch } = useLivePrices(openSymbols);

  // ─── "Working Now" metric strip + winners split (Feb 22 2026) ─────
  // User: "stop hiding successes". Split OPEN alerts into buy-actionable
  // (fresh) and already-working (winners), surface EQUALLY — no opacity
  // dimming, no collapse. Two-panel layout below the metric strip.
  const workingStats = useMemo(() => {
    const all = [...activeFresh, ...activeStale];
    let inProfit = 0, bestRet = null, bestSym = null, sumRet = 0, nWithRet = 0;
    for (const a of all) {
      const live = mergeLiveIntoAlert(a, livePrices);
      const r = typeof live.return_pct === "number" ? live.return_pct : null;
      if (r == null) continue;
      nWithRet += 1;
      sumRet += r;
      if (r > 0) inProfit += 1;
      if (bestRet == null || r > bestRet) { bestRet = r; bestSym = a.symbol; }
    }
    return {
      total: all.length,
      inProfit,
      avgReturn: nWithRet ? sumRet / nWithRet : null,
      bestRet, bestSym,
    };
  }, [activeFresh, activeStale, livePrices]);

  // ─── Reclassify winners that dropped back below entry (Feb 2026) ───
  // A ticker that already crossed TP1 but has since fallen back under its
  // entry price isn't "already moved" any more — it's either a fresh
  // re-entry setup or heading to the stop. Either way, surface it in the
  // left (actionable) column so the subscriber reads it, not past it.
  const { freshDisplay, staleDisplay } = useMemo(() => {
    const fresh = [...activeFresh];
    const stale = [];
    for (const a of activeStale) {
      const live = livePrices?.[a.symbol];
      const entry = typeof a.entry_price === "number" ? a.entry_price : null;
      const px = live && typeof live.price === "number" ? live.price : null;
      if (entry && px && px < entry) {
        // Winner has coughed up its gain — move back to actionable column
        // with a flag the AlertRow can use to badge it (optional).
        fresh.push({ ...a, _reentry_candidate: true });
      } else {
        stale.push(a);
      }
    }
    return { freshDisplay: fresh, staleDisplay: stale };
  }, [activeFresh, activeStale, livePrices]);

  const workingWinners = useMemo(
    () => [...staleDisplay].sort((x, y) => {
      const rx = mergeLiveIntoAlert(x, livePrices).return_pct ?? -999;
      const ry = mergeLiveIntoAlert(y, livePrices).return_pct ?? -999;
      return ry - rx;
    }),
    [staleDisplay, livePrices],
  );

  const activeCount = freshDisplay.length + staleDisplay.length;
  const universeSize = regime?.universe_size ?? topPicks.length ? "297" : "—";
  const regimeLabel = (regime?.regime || "—").toUpperCase();

  return (
    <div className="min-h-screen relative" style={{ zIndex: 1 }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">

        {/* HUD telemetry strip */}
        <div className="mb-telemetry mb-8" data-testid="hud-telemetry">
          <span className="flex items-center gap-2">
            <span className="mb-status-dot" />
            <span style={{ color: "var(--mb-emerald)" }}>SYS · ONLINE</span>
          </span>
          <span>SESSION {clock}</span>
          <span>REGIME · {regimeLabel}</span>
          <span>UNIVERSE · {universeSize} SYMS</span>
          <span>NEXT SCAN · 12:00 EST</span>
          {liveLastFetch && (
            <span data-testid="live-ticker-indicator" title="Live prices from Finnhub, refreshed every 30s">
              <span className="mb-status-dot" style={{ background: "#4ADE80" }} />
              <span style={{ color: "#4ADE80" }}> LIVE</span>
              <span style={{ color: "var(--mb-ink-muted)" }}> · updated {Math.max(0, Math.floor((Date.now() - liveLastFetch.getTime()) / 1000))}s ago</span>
            </span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-8">
          <div>
            <div className="label-uppercase" style={{ color: "var(--mb-emerald)", letterSpacing: "0.28em" }}>DASHBOARD</div>
            <h1 className="font-display font-medium text-4xl sm:text-5xl tracking-tight mt-1.5" data-testid="dashboard-greeting" style={{ color: "var(--mb-ink)" }}>
              {greetingFor(new Date())}, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="mt-1 text-sm" style={{ color: "var(--mb-ink-muted)" }}>
              {isSubscriber ? "Live alerts from the analyst desk." : "Subscribe to unlock vetted BUY alerts."}
            </p>
          </div>
          <Button variant="outline" onClick={() => load({ fresh: true })} className="rounded-md mb-btn-ghost" data-testid="dashboard-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <MacroBriefing />
        <SectorStrip />
        {isSubscriber && (
          <>
            <RecentExits authHeader={{ Authorization: `Bearer ${authLib.getToken()}` }} />
          </>
        )}

        <div className="space-y-6 mt-6">
          <section className="mb-card p-6" data-testid="alert-feed-section">
            <HudCorners />
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-uppercase" style={{ color: "var(--mb-ink-muted)", letterSpacing: "0.28em" }}>LIVE ALERT FEED</div>
                <h2 className="font-display font-semibold text-2xl mt-1.5" style={{ color: "var(--mb-ink)" }}>Published alerts</h2>
              </div>
              {isSubscriber && feed.length > 0 && (
                <div className="flex items-center gap-2 font-mono-data text-[11px]" style={{ color: "var(--mb-emerald)" }}>
                  <span className="mb-status-dot" />
                  {activeCount} ACTIVE
                </div>
              )}
            </div>

            {!isSubscriber && (
              <div className="p-6 rounded-md mb-card-elevated text-center relative" data-testid="paywall-banner">
                <HudCorners color="rgba(168, 85, 247, 0.35)" />
                <div className="font-display font-semibold text-xl" style={{ color: "var(--mb-emerald)" }}>Subscriber-only</div>
                <p className="text-sm mt-2" style={{ color: "var(--mb-ink-muted)" }}>Approved BUY/SELL alerts go straight to subscribers — for less than a cup of coffee.</p>
                <Link to="/app/account">
                  <Button className="mt-4 mb-btn-primary rounded-md" data-testid="subscribe-cta-feed">
                    Activate Moonbucks
                  </Button>
                </Link>
              </div>
            )}

            {isSubscriber && loadingFeed && (
              <div className="flex items-center gap-2 py-12 justify-center" style={{ color: "var(--mb-ink-muted)" }}>
                <Loader2 className="w-4 h-4 animate-spin" /> Loading alerts…
              </div>
            )}

            {isSubscriber && !loadingFeed && feed.length === 0 && (
              <div className="py-12 text-center" style={{ color: "var(--mb-ink-muted)" }} data-testid="empty-feed">
                <Activity className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--mb-ink-subtle)" }} />
                No alerts yet. The next one drops the moment conditions align.
              </div>
            )}

            {isSubscriber && !loadingFeed && feed.length > 0 && (
              <>
                <div className="flex gap-1 mb-4 rounded-md p-1 w-fit" style={{ backgroundColor: "var(--mb-surface-elevated)", border: "1px solid var(--mb-border)" }} data-testid="feed-tabs">
                  <button
                    onClick={() => setFeedTab("active")}
                    className={`px-4 py-1.5 rounded-md text-xs font-bold tracking-wider transition ${feedTab === "active" ? "" : "hover:text-mb-ink"}`}
                    style={feedTab === "active"
                      ? { backgroundColor: "var(--mb-emerald)", color: "#030110", boxShadow: "0 0 18px rgba(168,85,247,0.4)" }
                      : { color: "var(--mb-ink-muted)" }}
                    data-testid="feed-tab-active"
                  >
                    ACTIVE ({activeCount})
                  </button>
                  <button
                    onClick={() => setFeedTab("history")}
                    className="px-4 py-1.5 rounded-md text-xs font-bold tracking-wider transition"
                    style={feedTab === "history"
                      ? { backgroundColor: "var(--mb-ink)", color: "#030110" }
                      : { color: "var(--mb-ink-muted)" }}
                    data-testid="feed-tab-history"
                  >
                    HISTORY ({historyClosed.length})
                  </button>
                </div>

                {feedTab === "active" && (
                  <>
                    {freshDisplay.length === 0 && staleDisplay.length === 0 && (
                      <div className="py-8 text-center text-sm" style={{ color: "var(--mb-ink-muted)" }}>
                        No active alerts right now. Check History for past picks.
                      </div>
                    )}
                    {/* Working Now strip — instant proof the signals are working. */}
                    {workingStats.total > 0 && (
                      <div
                        className="mb-6 rounded-lg border p-4 flex flex-wrap items-center gap-6"
                        style={{
                          background: "linear-gradient(135deg, rgba(74,222,128,0.10), rgba(168,85,247,0.06))",
                          borderColor: "rgba(74,222,128,0.30)",
                        }}
                        data-testid="working-now-strip"
                      >
                        <div>
                          <div className="label-uppercase text-[10px] tracking-widest" style={{ color: "var(--mb-ink-muted)" }}>
                            Alerts working now
                          </div>
                          <div className="font-mono-data text-2xl font-bold mt-0.5" style={{ color: "#4ADE80" }} data-testid="working-in-profit">
                            {workingStats.inProfit}<span className="opacity-50 text-lg">/{workingStats.total}</span>
                            <span className="text-xs uppercase tracking-wider ml-2" style={{ color: "var(--mb-ink-muted)" }}>in profit</span>
                          </div>
                        </div>
                        {workingStats.avgReturn != null && (
                          <div>
                            <div className="label-uppercase text-[10px] tracking-widest" style={{ color: "var(--mb-ink-muted)" }}>Avg return</div>
                            <div className={`font-mono-data text-2xl font-bold mt-0.5 ${workingStats.avgReturn >= 0 ? "" : ""}`}
                                 style={{ color: workingStats.avgReturn >= 0 ? "#4ADE80" : "#FF5C5C" }}
                                 data-testid="working-avg-return">
                              {workingStats.avgReturn >= 0 ? "+" : ""}{workingStats.avgReturn.toFixed(2)}%
                            </div>
                          </div>
                        )}
                        {workingStats.bestSym && (
                          <div>
                            <div className="label-uppercase text-[10px] tracking-widest" style={{ color: "var(--mb-ink-muted)" }}>Best trade</div>
                            <div className="font-mono-data text-2xl font-bold mt-0.5" style={{ color: "#4ADE80" }} data-testid="working-best">
                              {workingStats.bestSym} <span className="text-lg">+{workingStats.bestRet.toFixed(1)}%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Two-panel layout — buy-actionable | already-working, equal weight */}
                    <div className="grid md:grid-cols-2 gap-4" data-testid="two-panel-feed">
                      <div data-testid="panel-in-zone">
                        <div className="label-uppercase text-base sm:text-lg font-bold tracking-[0.18em] mb-3 pb-2 flex items-center gap-2.5 border-b-2"
                             style={{ color: "#4ADE80", borderColor: "#4ADE80" }}>
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#4ADE80", boxShadow: "0 0 8px #4ADE80" }} />
                          IN ENTRY ZONE · {freshDisplay.length} BUY-ACTIONABLE
                        </div>
                        {freshDisplay.length > 0 ? (
                          <ul className="space-y-3">
                            {freshDisplay.map((a) => <AlertRow key={a.id} alert={a} livePrices={livePrices} />)}
                          </ul>
                        ) : (
                          <div className="rounded-lg border border-dashed p-6 text-center text-sm"
                               style={{ borderColor: "var(--mb-border)", color: "var(--mb-ink-muted)" }}>
                            No fresh entries right now. Scanner runs continuously — new picks queued for approval will appear here.
                          </div>
                        )}
                      </div>
                      <div data-testid="panel-working">
                        <div className="label-uppercase text-base sm:text-lg font-bold tracking-[0.18em] mb-3 pb-2 flex items-center gap-2.5 border-b-2"
                             style={{ color: "#A855F7", borderColor: "#A855F7" }}>
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#A855F7", boxShadow: "0 0 8px #A855F7" }} />
                          WORKING NOW · {workingWinners.length} ALREADY MOVED
                        </div>
                        {workingWinners.length > 0 ? (
                          <ul className="space-y-3">
                            {workingWinners.map((a) => <AlertRow key={a.id} alert={a} livePrices={livePrices} />)}
                          </ul>
                        ) : (
                          <div className="rounded-lg border border-dashed p-6 text-center text-sm"
                               style={{ borderColor: "var(--mb-border)", color: "var(--mb-ink-muted)" }}>
                            None yet — winners will show here once alerts clear entry zone.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {feedTab === "history" && (
                  <>
                    {historyClosed.length === 0 && (
                      <div className="py-8 text-center text-sm" style={{ color: "var(--mb-ink-muted)" }}>No closed alerts yet.</div>
                    )}
                    {historyClosed.length > 0 && (
                      <ul className="space-y-2" data-testid="history-list">
                        {historyClosed.map((a) => <AlertRow key={a.id} alert={a} muted />)}
                      </ul>
                    )}
                  </>
                )}
              </>
            )}
          </section>

          <aside className={isSubscriber ? "grid lg:grid-cols-2 gap-6" : "space-y-6"}>
            {/* Pressure screener — top 5 building up. Available to any paying
                subscriber (or admin). Free-tier users still see the CTA-only
                dashboard so they get value promise without giving away the
                live picks. */}
            {isSubscriber && (
              <div className="mb-card p-6 relative" data-testid="top-picks-section">
                <HudCorners />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="label-uppercase" style={{ color: "var(--mb-ink-muted)", letterSpacing: "0.28em" }}>PRESSURE SCREENER</div>
                    <h2 className="font-display font-semibold text-xl mt-1.5" style={{ color: "var(--mb-ink)" }}>Top 5 building up</h2>
                  </div>
                  <TrendingUp className="w-5 h-5" style={{ color: "var(--mb-emerald)" }} />
                </div>

                {topPicks.length === 0 ? (
                  <p className="mt-6 text-sm" style={{ color: "var(--mb-ink-muted)" }}>No BUY candidates passing both stages right now. Check back soon.</p>
                ) : (
                  <ul className="mt-5 space-y-2">
                    {topPicks.map((p) => (
                      <Link
                        to={`/app/stock/${p.symbol}`}
                        key={`${p.symbol}-${p.alert_class || "steady"}`}
                        className="flex items-center justify-between p-3 rounded-md transition border"
                        style={{ borderColor: "var(--mb-border)", backgroundColor: "var(--mb-surface-elevated)" }}
                        data-testid={`top-pick-${p.symbol}`}
                      >
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono-data font-bold" style={{ color: "var(--mb-ink)" }}>{p.symbol}</span>
                            <AlertClassBadge alertClass={p.alert_class || "steady"} compact />
                            <ScorePill grade={p.moonbucks_grade} size="xs" />
                          </div>
                          <div className="text-[11px] mt-0.5" style={{ color: "var(--mb-ink-muted)" }}>
                            ${p.indicators.last_price} · RSI {p.indicators.rsi}
                          </div>
                        </div>
                        <SignalBadge action={p.decision.action} confidence={p.decision.confidence} size="sm" />
                      </Link>
                    ))}
                  </ul>
                )}
                <Link to="/app/screener">
                  <Button variant="ghost" className="mt-4 w-full rounded-md mb-btn-ghost" data-testid="view-full-screener">
                    View full screener →
                  </Button>
                </Link>
              </div>
            )}

            <div className="mb-card-elevated p-6 relative">
              <HudCorners color="rgba(168, 85, 247, 0.35)" />
              <div className="label-uppercase" style={{ color: "var(--mb-ink-muted)", letterSpacing: "0.28em" }}>YOUR PORTFOLIO</div>
              <CustomScenarioSummary summary={customSummary} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function formatClock() {
  const d = new Date();
  return d.toTimeString().slice(0, 8) + " " + (Intl.DateTimeFormat().resolvedOptions().timeZone.split("/").pop() || "");
}

// Sidebar summary card for the subscriber's saved custom portfolio.
// - null: still loading (spinner).
// - false / no saved: empty-state CTA that deep-links to /portfolio.
// - object with .summary: mini snapshot (return, vs SPY, trades, picks).
function CustomScenarioSummary({ summary }) {
  if (summary === null) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: "var(--mb-ink-muted)" }} data-testid="custom-summary-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading your scenario…
      </div>
    );
  }
  if (!summary || !summary.summary) {
    return (
      <>
        <h3 className="font-display font-semibold text-xl mt-1.5" style={{ color: "var(--mb-ink)" }}>Build your custom scenario</h3>
        <p className="text-sm mt-2" style={{ color: "var(--mb-ink-muted)" }}>
          Hand-pick which alerts belong in <em>your</em> portfolio and re-run the same simulation math. Your picks stay saved to your account.
        </p>
        <Link to="/portfolio">
          <Button variant="outline" className="mt-4 rounded-md mb-btn-ghost" data-testid="goto-custom-portfolio-btn">Pick your alerts →</Button>
        </Link>
      </>
    );
  }
  const s = summary.summary;
  const ret = Number(s.return_pct || 0);
  const spy = Number(s.spy_return_pct || 0);
  const delta = ret - spy;
  const posRet = ret >= 0;
  const beatsSpy = delta >= 0;
  const equity = Number(s.final_equity || 1000);
  const savedWhen = summary.saved_at ? new Date(summary.saved_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;

  return (
    <div data-testid="custom-summary-card">
      <div className="flex items-center justify-between mt-1.5">
        <h3 className="font-display font-semibold text-xl" style={{ color: "var(--mb-ink)" }}>Your custom portfolio</h3>
        <span className="text-[10px] font-mono-data" style={{ color: "var(--mb-ink-muted)" }}>
          {summary.picked_count} pick{summary.picked_count === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-md px-3 py-2" style={{ backgroundColor: "var(--mb-surface-elevated)", border: "1px solid var(--mb-border)" }}>
          <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--mb-ink-muted)" }}>Return</div>
          <div className="font-mono-data text-xl font-bold" style={{ color: posRet ? "var(--mb-emerald)" : "#ef4444" }} data-testid="custom-summary-return">
            {posRet ? "+" : ""}{ret.toFixed(2)}%
          </div>
        </div>
        <div className="rounded-md px-3 py-2" style={{ backgroundColor: "var(--mb-surface-elevated)", border: "1px solid var(--mb-border)" }}>
          <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--mb-ink-muted)" }}>vs SPY</div>
          <div className="font-mono-data text-xl font-bold" style={{ color: beatsSpy ? "var(--mb-emerald)" : "#f59e0b" }} data-testid="custom-summary-vs-spy">
            {beatsSpy ? "+" : ""}{delta.toFixed(2)}pp
          </div>
          <div className="text-[9px] font-mono-data mt-0.5" style={{ color: "var(--mb-ink-muted)" }}>
            SPY {spy >= 0 ? "+" : ""}{spy.toFixed(2)}%
          </div>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]" style={{ color: "var(--mb-ink-muted)" }}>
        <div>
          <span className="font-mono-data" style={{ color: "var(--mb-ink)" }}>${equity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
          <span className="ml-1">on $10,000</span>
        </div>
        <div className="text-right">
          <span className="font-mono-data" style={{ color: "var(--mb-ink)" }}>{s.trades_count ?? 0}</span> closed · <span className="font-mono-data" style={{ color: "var(--mb-ink)" }}>{s.still_open ?? 0}</span> open
        </div>
      </div>

      {savedWhen && (
        <div className="mt-2 text-[10px] font-mono-data" style={{ color: "var(--mb-ink-muted)" }}>
          Last saved · {savedWhen}
        </div>
      )}

      <Link to="/portfolio">
        <Button variant="outline" className="mt-4 w-full rounded-md mb-btn-ghost" data-testid="edit-custom-portfolio-btn">Edit picks →</Button>
      </Link>
    </div>
  );
}


function RegimeBanner({ regime }) {
  // Deprecated — replaced by MacroBriefing (Global Sentiment Engine). Kept only
  // to avoid stale imports; safe to delete in a follow-up.
  return null;
}

function AlertRow({ alert, muted = false, livePrices }) {
  const cls = alert.alert_class || "steady";
  const isMoon = cls === "moonshot";
  const live = mergeLiveIntoAlert(alert, livePrices);
  const accent = isMoon ? "#FF4D9D" : "#A855F7";
  const dateLabel = new Date(alert.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const padding = muted ? "p-3" : "p-4";
  const grade = isMoon ? alert.conviction_grade : null;

  return (
    <li
      className={`${padding} rounded-md transition fade-in-up relative ${muted ? "" : ""}`}
      style={{
        backgroundColor: "var(--mb-surface-elevated)",
        border: `1px solid ${isMoon ? "rgba(255, 77, 157, 0.30)" : "var(--mb-border)"}`,
        boxShadow: muted
          ? "none"
          : isMoon
          ? "0 18px 40px -28px rgba(255, 77, 157, 0.45), inset 0 1px 0 0 rgba(255, 77, 157, 0.10)"
          : "0 16px 36px -28px rgba(168, 85, 247, 0.35), inset 0 1px 0 0 rgba(168, 85, 247, 0.10)",
      }}
      data-testid={`alert-row-${alert.symbol}`}
    >
      {!muted && <HudCorners color={isMoon ? "rgba(255, 77, 157, 0.55)" : "rgba(168, 85, 247, 0.3)"} />}
      <Link to={`/app/stock/${alert.symbol}`} className="block group">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{
                backgroundColor: accent,
                boxShadow: `0 0 12px ${accent}, 0 0 2px ${accent}`,
              }}
              title={isMoon ? "Moonshot" : "Steady"}
              data-testid={`class-dot-${cls}`}
            />
            <span className="font-mono-data font-bold text-lg" style={{ color: "var(--mb-ink)", letterSpacing: "0.04em" }} data-testid={`alert-symbol-${alert.symbol}`}>
              {alert.symbol}
            </span>
            <AlertStatusBadge live={live} alertClass={cls} compact />
            <ScorePill grade={alert.moonbucks_grade} size="xs" />
            {grade && (
              <span
                className="font-mono-data text-[10px] font-extrabold px-2 py-0.5 rounded ml-1"
                style={{
                  color: grade === "S" ? "#030110" : (grade === "A" ? "#030110" : "var(--mb-ink-subtle)"),
                  backgroundColor: grade === "S" ? "#FF4D9D" : (grade === "A" ? "#A855F7" : "transparent"),
                  border: grade === "B" ? "1px solid var(--mb-border)" : "none",
                  boxShadow: grade === "S" ? "0 0 14px rgba(255,77,157,0.55)" : (grade === "A" ? "0 0 12px rgba(168,85,247,0.45)" : "none"),
                  letterSpacing: "0.1em",
                }}
                title={grade === "S" ? "Tier S — Conviction Stack: 2+ smart-money signals firing" : (grade === "A" ? "Tier A — Conviction Stack: 1 smart-money signal firing" : "Tier B — Conviction Stack quiet")}
                data-testid={`tier-badge-${grade}`}
              >
                TIER {grade}
              </span>
            )}
            <NeutralMarketTag regime={alert.regime} testId={`neutral-market-tag-${alert.symbol}`} />
            {alert._reentry_candidate && (
              <span
                className="font-mono-data text-[10px] font-extrabold px-2 py-0.5 rounded ml-1 inline-flex items-center gap-1"
                style={{
                  color: "#030110",
                  backgroundColor: "#4ADE80",
                  letterSpacing: "0.1em",
                  boxShadow: "0 0 12px rgba(74, 222, 128, 0.55)",
                }}
                title="This alert ran, then gave back its gain and is now trading below entry — back in buy range or heading to stop"
                data-testid={`reentry-badge-${alert.symbol}`}
              >
                RE-ENTRY
              </span>
            )}
            {alert.sector && (
              <span
                className="font-mono-data text-[10px] px-2 py-0.5 rounded ml-1 hidden sm:inline-flex items-center gap-1"
                style={{
                  color: "var(--mb-ink-muted)",
                  backgroundColor: "var(--mb-surface)",
                  border: "1px solid var(--mb-border)",
                  letterSpacing: "0.06em",
                }}
                title={alert.industry ? `${alert.sector} · ${alert.industry}` : alert.sector}
                data-testid={`sector-chip-${alert.symbol}`}
              >
                <span>{alert.sector_emoji || "🏷️"}</span>
                <span>{alert.sector}</span>
                {alert.sector_etf && <span style={{ color: "var(--mb-ink-subtle)" }}>·{alert.sector_etf}</span>}
              </span>
            )}
          </div>
          <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
            {live.return_pct != null ? (
              <div className="flex flex-col items-end gap-0" data-testid="alert-row-pnl">
                <span
                  className="font-mono-data font-bold text-base"
                  style={{
                    color: live.return_pct >= 0 ? "#4ADE80" : "#FF5C5C",
                    textShadow: `0 0 10px ${live.return_pct >= 0 ? "#4ADE80" : "#FF5C5C"}55`,
                  }}
                  title="Live return since alert published"
                >
                  {live.return_pct >= 0 ? "+" : ""}{live.return_pct}%
                  <span className="ml-1 font-normal text-[9px] tracking-widest uppercase opacity-70">since entry</span>
                </span>
                {live.day_change_pct != null && (
                  <span
                    className="font-mono-data font-semibold text-[11px] leading-tight"
                    style={{ color: live.day_change_pct >= 0 ? "#4ADE80" : "#FF5C5C", opacity: 0.9 }}
                    data-testid="alert-row-day-change"
                    title="Move today (previous close → last)"
                  >
                    {live.day_change_pct >= 0 ? "+" : ""}{live.day_change_pct}%
                    <span className="ml-1 font-normal text-[9px] tracking-widest uppercase opacity-70">today</span>
                  </span>
                )}
              </div>
            ) : <span />}
            <span className="font-mono-data text-[10px] tracking-wide" style={{ color: "var(--mb-ink-subtle)" }}>
              {live.status === "ACTIVE" && live.days_remaining != null
                ? `DAY ${live.days_elapsed}/${live.max_hold_days} · ${dateLabel.toUpperCase()}`
                : dateLabel.toUpperCase()}
            </span>
          </div>
        </div>

        {alert.entry_price && !muted && (
          <div className="mt-4">
            <PriceContinuum
              entry={alert.entry_price}
              stop={alert.stop_loss_price}
              tp1={alert.take_profit_price}
              moon={alert.moon_target_price || alert.take_profit_price}
              now={live.current_price}
            />
          </div>
        )}
      </Link>

      {alert.admin_note && (
        <div className="mt-3 p-2 rounded text-xs border" style={{
          backgroundColor: "rgba(245, 166, 35, 0.10)",
          borderColor: "rgba(245, 166, 35, 0.30)",
          color: "#F5C56A",
        }}>
          <span className="font-semibold">Desk note:</span> {alert.admin_note}
        </div>
      )}

      {/* Share panel — only for closed winning alerts whose exit actually
          went out live (stale-purged trades never had a real exit). Uses the
          realized outcome, not the live-price overlay. */}
      {(() => {
        const realized = alert.live || {};
        const isWin = String(realized.status || "").startsWith("WIN")
          && Number(realized.return_pct) > 0
          && alert.closed_reason !== "stale_purge";
        return isWin ? <SharePanel alert={alert} /> : null;
      })()}
    </li>
  );
}
